import { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';

interface PromptState {
  /** Whether the modal is visible */
  visible: boolean;
  /** The callback to invoke when the user confirms navigation */
  onConfirm: (() => void) | null;
}

export const usePrompt = (
  when: boolean,
  message: string = 'You have unsaved changes. Are you sure you want to leave?'
) => {
  const history = useHistory();
  const unblockRef = useRef<(() => void) | null>(null);
  const [prompt, setPrompt] = useState<PromptState>({ visible: false, onConfirm: null });

  // Block navigation and show our custom modal instead of the browser confirm
  useEffect(() => {
    if (when) {
      unblockRef.current = history.block((location) => {
        // Show the custom modal and stash the target location so we can
        // navigate there if the user confirms.
        setPrompt({
          visible: true,
          onConfirm: () => {
            // Unblock first so the push goes through
            if (unblockRef.current) {
              unblockRef.current();
              unblockRef.current = null;
            }
            history.push(location.pathname + location.search + location.hash);
          },
        });
        // Returning false prevents the navigation
        return false;
      });
    } else {
      if (unblockRef.current) {
        unblockRef.current();
        unblockRef.current = null;
      }
    }

    return () => {
      if (unblockRef.current) {
        unblockRef.current();
        unblockRef.current = null;
      }
    };
  }, [when, history]);

  // Browser tab / window close — the native beforeunload dialog is unavoidable here
  useEffect(() => {
    const handler = (event: BeforeUnloadEvent) => {
      if (!when) return;
      event.preventDefault();
      event.returnValue = message;
      return message;
    };

    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [when, message]);

  const handleCancel = useCallback(() => {
    setPrompt({ visible: false, onConfirm: null });
  }, []);

  const handleConfirm = useCallback(() => {
    if (prompt.onConfirm) prompt.onConfirm();
    setPrompt({ visible: false, onConfirm: null });
  }, [prompt.onConfirm]);

  return {
    promptVisible: prompt.visible,
    promptMessage: message,
    onPromptConfirm: handleConfirm,
    onPromptCancel: handleCancel,
  };
};

export default usePrompt;
